import classNames from "classnames";
import { CloseIcon, SearchIcon } from "@icons";
import { styleWithVars } from "@lib/cssVars";
import type { ResolvedAnatomyBinding } from "./componentAnatomy";
import {
    bindingReader,
    Part,
    type AnatomyPartHighlight,
} from "./componentAnatomyParts";
import styles from "./ComponentAnatomyView.module.scss";

/**
 * What stands inside the input on the x-ray plate. The playground passes its
 * own sandbox feature state here, so the schematic always draws the same input
 * the live stage would show.
 */
export interface InputSchematicContent {
    text: string;
    placeholder: string;
    hasLeftIcon: boolean;
    isClearable: boolean;
}

const defaultContent: InputSchematicContent = {
    text: "Input",
    placeholder: "Placeholder",
    hasLeftIcon: true,
    isClearable: false,
};

/** X-ray plate for Input: left icon, filled text, clear button. */
export const InputSchematic = ({
    bindings,
    highlight,
    content = defaultContent,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
    content?: InputSchematicContent;
}) => {
    const read = bindingReader(bindings);
    const radius = read("border", "border-radius");
    const iconSize = read("glyph", "width");
    const xSize = read("xButton", "width");
    const xIconWidth = read("xButton", "icon width");
    const xIconHeight = read("xButton", "icon height");
    const ringSpread = read("focusRing", "box-shadow (spread)");
    const ringColor = read("focusRing", "box-shadow (color)");
    const paddingInline = read("background", "padding-inline");
    const isFilled = content.text !== "";
    const shouldShowX = content.isClearable && isFilled;

    return (
        <span
            className={classNames(styles.schematic, styles.buttonSchematic)}
            style={{ height: read("background", "height"), width: 240 }}
        >
            {ringSpread !== undefined && ringColor !== undefined && (
                <Part
                    partId="focusRing"
                    highlight={highlight}
                    className={styles.buttonRing}
                    hitInset={`calc(-1 * ${ringSpread})`}
                    style={{
                        borderRadius: radius,
                        boxShadow: `0 0 0 ${ringSpread} ${ringColor}`,
                    }}
                />
            )}
            <Part
                partId="border"
                highlight={highlight}
                className={styles.buttonBorder}
                style={{
                    borderColor: read("border", "border-color"),
                    borderWidth: read("border", "border-width"),
                    borderRadius: radius,
                }}
            />
            <Part
                partId="background"
                highlight={highlight}
                className={styles.buttonBackground}
                style={{
                    backgroundColor: read("background", "background-color"),
                    borderRadius: radius,
                }}
            />
            {/* The real .root sets color: var(--input-fg); the icon and the
                clear button inherit it, so only the label overrides it. */}
            <span
                className={classNames(
                    styles.buttonContent,
                    styles.inputContent,
                )}
                style={styleWithVars({
                    "--input-padding-x": paddingInline,
                    paddingInline,
                    paddingRight: shouldShowX
                        ? read("background", "padding-inline (with slot box)")
                        : undefined,
                    gap: read("background", "gap"),
                    color: read("label", "color"),
                    justifyContent: "flex-start",
                    width: "100%",
                })}
            >
                {content.hasLeftIcon && (
                    <Part
                        partId="glyph"
                        highlight={highlight}
                        className={styles.tagSlot}
                        style={{ width: iconSize, height: iconSize }}
                    >
                        <SearchIcon />
                    </Part>
                )}
                <Part
                    partId="label"
                    highlight={highlight}
                    className={styles.buttonLabel}
                    style={{
                        flex: 1,
                        color: isFilled
                            ? read("label", "color")
                            : read("label", "color (placeholder)"),
                        fontSize: read("label", "font-size"),
                        fontWeight: read("label", "font-weight"),
                        lineHeight: read("label", "line-height"),
                        letterSpacing: read("label", "letter-spacing"),
                    }}
                >
                    {isFilled ? content.text : content.placeholder}
                </Part>
                {shouldShowX && (
                    <Part
                        partId="xButton"
                        highlight={highlight}
                        className={styles.tagXButton}
                        style={{
                            width: xSize,
                            height: xSize,
                            borderRadius: read("xButton", "border-radius"),
                        }}
                    >
                        <CloseIcon width={xIconWidth} height={xIconHeight} />
                    </Part>
                )}
            </span>
        </span>
    );
};
